import { Check } from "lucide-react";
import type { ReactNode } from "react";
import { useOnboardingStatus } from "@/features/home/onboarding/hooks/use-onboarding-status";
import { useOnboardingActions } from "@/features/home/onboarding/onboarding.context";
import type { OnboardingStatus } from "@/features/home/onboarding/schemas/onboarding-status.schema";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { cn } from "@/shared/lib/cn";

interface OnboardingProgressProps {
  status: OnboardingStatus;
}

interface WorkspaceOnboardingProgressProps {
  workspaceId: string;
}

interface ProgressStepProps {
  label: string;
  done: boolean;
  actionLabel: string;
  onAction: () => void;
}

export function countCompletedOnboardingSteps(status: OnboardingStatus): number {
  return (
    Number(status.steps.importTeam) +
    Number(status.steps.giveKudos) +
    Number(status.steps.dailyCheckIn)
  );
}

export function WorkspaceOnboardingProgress({
  workspaceId,
}: WorkspaceOnboardingProgressProps): ReactNode {
  const onboarding = useOnboardingStatus(workspaceId);
  if (!onboarding.data) return null;
  return <OnboardingProgress status={onboarding.data} />;
}

export function OnboardingProgress({ status }: OnboardingProgressProps): ReactNode {
  const { openCheckIn, openGiveKudos, openImportTeam } = useOnboardingActions();

  if (!status.applicable || status.complete) return null;

  const completedCount = countCompletedOnboardingSteps(status);
  const nextStep = !status.steps.importTeam
    ? "Import your team"
    : !status.steps.giveKudos
      ? "Give kudos"
      : "Daily check-in";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="hidden gap-2 lg:inline-flex">
          <span className="font-medium">
            Workspace setup {completedCount}/3: {nextStep}
          </span>
          <span className="flex items-center gap-1" aria-hidden="true">
            <ProgressSegment active={status.steps.importTeam} />
            <ProgressSegment active={status.steps.giveKudos} />
            <ProgressSegment active={status.steps.dailyCheckIn} />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96">
        <PopoverHeader>
          <PopoverTitle>Finish your workspace setup</PopoverTitle>
          <PopoverDescription>
            These same three onboarding actions stay reachable from anywhere in the app.
          </PopoverDescription>
        </PopoverHeader>
        <ol className="mt-4 flex flex-col gap-3" aria-label="Setup progress">
          <ProgressStep
            label="Import your team"
            done={status.steps.importTeam}
            actionLabel="Import team"
            onAction={openImportTeam}
          />
          <ProgressStep
            label="Write kudos"
            done={status.steps.giveKudos}
            actionLabel="Write kudos"
            onAction={openGiveKudos}
          />
          <ProgressStep
            label="Create daily check-in"
            done={status.steps.dailyCheckIn}
            actionLabel="Create check-in"
            onAction={openCheckIn}
          />
        </ol>
      </PopoverContent>
    </Popover>
  );
}

function ProgressSegment({ active }: { active: boolean }): ReactNode {
  return (
    <span
      className={cn(
        "h-1.5 w-6 rounded-full transition-colors",
        active ? "bg-primary" : "bg-surface-muted",
      )}
    />
  );
}

function ProgressStep({ label, done, actionLabel, onAction }: ProgressStepProps): ReactNode {
  return (
    <li className="flex items-center justify-between gap-3 rounded-lg bg-surface-subtle px-3 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <span
          className={cn(
            "flex size-5 shrink-0 items-center justify-center rounded-full ring-1 ring-inset",
            done ? "bg-success-bg ring-success-bg" : "bg-background ring-border-strong",
          )}
          aria-hidden="true"
        >
          {done ? <Check className="size-3 text-success-fg" strokeWidth={2.5} /> : null}
        </span>
        <span
          className={cn(
            "min-w-0 text-sm font-medium",
            done ? "text-text-secondary line-through" : "text-text-primary",
          )}
        >
          {label}
        </span>
      </div>
      {done ? (
        <Badge variant="secondary" className="border-0 bg-success-bg text-success-fg">
          Done
        </Badge>
      ) : (
        <Button size="sm" variant="outline" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </li>
  );
}
